import React, { useMemo, useState, useCallback } from 'react'

import { Button, Popconfirm, Icon, Table, Row } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'
import * as yup from 'yup'

import CustomInput from '../../../../components/CustomInput/customInput'
import CustomSelect from '../../../../components/CustomSelect/customSelect'
import formatYupError from '../../../../util/formatYupError'
import { getMessage } from '../../../../util/IntlMessages'
import { ConfirmationModal } from '../common'
import _ from 'lodash'

const commercialProcessesItemValidation = yup.object().shape({
  predictedCommercialProcesses: yup.array().of(yup.string()).required(getMessage('mandatoryField')),
  area: yup.number().required(getMessage('mandatoryField')).positive(),
  month: yup.date().required(getMessage('mandatoryField'))
})

const commercialProcess = [
  { value: 'Silo', description: getMessage('forecast.commercial.processes.silo') },
  { value: 'Volume adicional', description: getMessage('forecast.commercial.processes.additional.volume') },
  { value: 'Pré-certificação', description: getMessage('forecast.commercial.processes.pre.certification') },
  { value: 'Segregação de carga', description: getMessage('forecast.commercial.processes.cargo.segregation') },
  { value: 'Cashback', description: getMessage('forecast.commercial.processes.cashback') },
  { value: 'Transferência', description: getMessage('forecast.commercial.processes.transfer') },
  { value: 'Reembolso', description: getMessage('forecast.commercial.processes.refund') },
  { value: 'Auditoria', description: getMessage('forecast.commercial.processes.audit') }
]

const CommercialProcessesItems = function (props) {
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})

  const dataSource = useMemo(
    () => props.commercialProcessesItems.map((item) => ({ ...item, key: item.id })),
    [JSON.stringify(props.commercialProcessesItems)]
  )

  const columns = useMemo(
    () => [
      {
        title: getMessage('predictedCommercialProcesses'),
        dataIndex: 'predictedCommercialProcesses',
        key: 'predictedCommercialProcesses',
        width: '31%',
        align: 'center',
        render: (items) => <span>{items.join(', ')}</span>
      },
      {
        title: getMessage('month'),
        dataIndex: 'month',
        key: 'month',
        width: '31%',
        align: 'center',
        render: (month) => month && <span>{moment(month).utc().format('MM/YYYY')}</span>
      },
      {
        title: getMessage('area'),
        dataIndex: 'area',
        key: 'area',
        width: '31%',
        align: 'center',
        render: (value) => parseInt(value, 10)
      },
      {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
        width: '7%',
        align: 'center',
        render: (text, record) => (
          <Popconfirm title={getMessage('SureToDelete?')} onConfirm={() => props.onDeleteItem(record.id)}>
            <Icon type="delete" theme="outlined" className="trash-icon" />
          </Popconfirm>
        )
      }
    ],
    [props.onDeleteItem, dataSource]
  )

  const handleChange = useCallback(
    (field, value) => {
      setValues((preValues) => ({ ...preValues, [field]: value }))
    },
    [values]
  )

  const handleAddItem = useCallback(() => {
    const matchingItem = props.commercialProcessesItems.find(
      (item) =>
        Array.isArray(item.predictedCommercialProcesses) &&
        Array.isArray(values.predictedCommercialProcesses) &&
        _.isEqual(item.predictedCommercialProcesses, values.predictedCommercialProcesses)
    )

    if (matchingItem) {
      ConfirmationModal(createSoyAreaItem, 'addForecastItems')
    } else {
      createSoyAreaItem()
    }
  }, [values])

  function createSoyAreaItem() {
    commercialProcessesItemValidation
      .validate(values, { abortEarly: false })
      .then(() => {
        props.onAddItem(values)
        setValues({})
        setErrors({})
      })
      .catch((error) => setErrors(formatYupError(error)))
  }

  return (
    <Row type="flex" justify="start">
      <CustomSelect
        showSearch
        field="predictedCommercialProcesses"
        errors={errors}
        setFieldValue={handleChange}
        rowSpan={6}
        value={values}
        values={commercialProcess}
        handleBlur={props.handleBlur}
        multiple={true}
      />
      <CustomInput
        type="date"
        field="month"
        errors={errors}
        setFieldValue={handleChange}
        rowSpan={6}
        value={values}
        format="DD/MM/YYYY"
        handleBlur={props.handleBlur}
      />
      <CustomInput
        field="area"
        errors={errors}
        setFieldValue={handleChange}
        rowSpan={6}
        value={values}
        type="number"
        step={0.000001}
        defaultValue={0}
        handleBlur={props.handleBlur}
      />
      <Button type="primary" className="button-action-plus" onClick={handleAddItem}>
        <Icon type="plus" />
      </Button>
      <Table bordered className="intacta-reserved-table" columns={columns} dataSource={dataSource} />
    </Row>
  )
}

CommercialProcessesItems.propTypes = {
  commercialProcessesItems: PropTypes.array.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired
}

export default CommercialProcessesItems
